// The live cat positioning signal of the original Cat Derby (retro.kissaderby.fi): a public WebSocket, read directly from the browser.
// Cloudflare drops the socket routinely (code 1001), so it reconnects with backoff 1 s, 2 s, 4 s … 15 s,
// like the site. A socket that stays open but silent for 10 s is treated as dropped.
import type { RaceSource, SignalFrame } from '../../race/types';
import { decode } from '../decode';
import { LIVE_LABEL, realScheduler, type Scheduler, type SourceStatus } from './types';

export const DEFAULT_LIVE_URL = 'wss://retro.kissaderby.fi/catderby';

/** The feed address: VITE_SIGNAL_URL at build time if set, else the retro site's feed. Pure, for tests. */
export function liveUrl(env: { VITE_SIGNAL_URL?: string }): string {
  return env.VITE_SIGNAL_URL?.trim() || DEFAULT_LIVE_URL;
}

export const LIVE_URL = liveUrl(import.meta.env);
export const LIVE_PROTOCOL = 'MAU';
export const BACKOFF_START_MS = 1000;
export const BACKOFF_MAX_MS = 15000;
export const SILENCE_MS = 10000;

/** The part of the WebSocket API this source uses. */
export interface SocketLike {
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  close(): void;
}
export type SocketFactory = (url: string, protocol: string) => SocketLike;

const browserSocket: SocketFactory = (url, protocol) => new WebSocket(url, protocol) as unknown as SocketLike;

export function nextBackoff(attempt: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_START_MS * 2 ** attempt);
}

export class LiveSource implements RaceSource {
  readonly kind = 'live' as const;
  readonly label = LIVE_LABEL;
  private socket: SocketLike | null = null;
  private attempt = 0;
  private retry: unknown = null;
  private silence: unknown = null;
  private running = false;
  private onFrame: (f: SignalFrame, arrivalMs: number) => void = () => {};
  private onStatus: (s: SourceStatus) => void = () => {};

  constructor(
    private readonly url = LIVE_URL,
    private readonly makeSocket: SocketFactory = browserSocket,
    private readonly sched: Scheduler = realScheduler,
  ) {}

  start(onFrame: (f: SignalFrame, arrivalMs: number) => void, onStatus: (s: SourceStatus) => void): void {
    if (this.running) return;
    this.running = true;
    this.onFrame = onFrame;
    this.onStatus = onStatus;
    this.connect('connecting');
  }

  stop(): void {
    this.running = false;
    this.sched.clearTimeout(this.retry);
    this.sched.clearTimeout(this.silence);
    this.retry = this.silence = null;
    this.drop();
  }

  private connect(status: SourceStatus): void {
    if (!this.running) return;
    this.onStatus(status);
    let ws: SocketLike;
    try {
      ws = this.makeSocket(this.url, LIVE_PROTOCOL);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;
    ws.onopen = () => { if (this.socket === ws) { this.onStatus('open'); this.armSilence(); } };
    ws.onmessage = (ev) => {
      if (this.socket !== ws || typeof ev.data !== 'string') return;
      const frame = decode(ev.data);
      if (!frame) return;
      this.attempt = 0; // a frame proves the connection; the next drop starts from 1 s again
      this.armSilence();
      this.onFrame(frame, this.sched.now());
    };
    ws.onerror = () => { /* a close event follows */ };
    ws.onclose = () => { if (this.socket === ws) { this.socket = null; this.scheduleReconnect(); } };
  }

  private armSilence(): void {
    this.sched.clearTimeout(this.silence);
    this.silence = this.sched.setTimeout(() => {
      if (!this.socket) return;
      this.drop();
      this.scheduleReconnect();
    }, SILENCE_MS);
  }

  private drop(): void {
    const ws = this.socket;
    this.socket = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try { ws.close(); } catch { /* already closed */ }
    }
  }

  private scheduleReconnect(): void {
    if (!this.running) return;
    this.sched.clearTimeout(this.silence);
    this.sched.clearTimeout(this.retry);
    const wait = nextBackoff(this.attempt++);
    this.onStatus('reconnecting');
    this.retry = this.sched.setTimeout(() => { this.retry = null; this.connect('reconnecting'); }, wait);
  }
}
